// A billing run read the way a program-integrity reviewer would read it.
//
// Pre-flight (edi/preflight.js) asks "can this file be built and accepted?".
// This asks a different question: "what in this run would make a reviewer look
// harder at AHFC?". State Medicaid agencies screen every provider's claims with
// software — impossible days, clients billed while not in service, hours above
// the authorisation, one nurse in two homes at once — and a pattern alone is
// enough to suspend payment before anyone requests a record (42 CFR 455.23).
//
// Run across 19 Jul – 17 Oct 2026 by hand first (2026-10-19), this found a 28-
// hour day (G091, 2 Oct), a discharged and an on-hold client still billed on
// their scheduled hours, about three in ten hours with no clock-in at all, and
// agency nurses booked in two homes at the same time. Every check below is one
// of those, so the next run is read before it is relied on.
//
// It advises; it never blocks and never corrects. Some of what it finds is
// legitimate (a split shift, a caregiver for siblings in one home) and that is
// for a person to say. Pure functions over rows the caller has loaded, so the
// rules are tested on their own.

import { resolvePriorAuth, authorizationInForce } from './priorAuth.js';
import { IN_SERVICE_STATUSES } from './serviceModel.js';
import { clockWindow } from './visitHours.js';

const day = (v) => (typeof v === 'string' && v.length >= 10 ? v.slice(0, 10) : null);
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const r1 = (n) => Math.round(n * 10) / 10;
const hrs = (n) => `${r1(n)}h`;

/** The Sunday a date's week starts on — billing and payroll weeks run Sun–Sat. */
export const weekOf = (iso) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  return d.toISOString().slice(0, 10);
};

/**
 * Which half of a GAPP authorisation a procedure code draws on. A PA
 * authorises skilled nursing (RN S9123, LPN S9124) and personal care support
 * (S9122, every modifier) as separate weekly figures.
 */
export const authBucket = (code) => {
  const c = String(code || '').toUpperCase();
  if (c === 'S9123' || c === 'S9124') return 'skilled';
  if (c === 'S9122') return 'pss';
  return null;
};

/** Weekly hours authorised for a client, on a date, for one bucket. Null = unknown. */
export const authorisedWeeklyHours = ({ history = [], client = null, date, bucket }) => {
  const field = bucket === 'skilled' ? 'skilled_hours_week' : 'pss_hours_week';
  // Which authorisation covers the date is decided in one place, priorAuth.js.
  // Hours can sit on a row with no number yet, so the number is not required.
  const inForce = authorizationInForce(history, date, { requireNumber: false });
  const fromHistory = inForce?.[field];
  if (fromHistory != null && fromHistory !== '') return num(fromHistory);
  // Clients whose authorisations predate the log carry the figure on the
  // client row only. Use it only while that authorisation was plausibly valid.
  const cached = client?.[bucket === 'skilled' ? 'pa_skilled_hours_week' : 'pa_pss_hours_week'];
  const exp = day(client?.pa_expiry_date);
  if (cached != null && cached !== '' && (!exp || exp >= date)) return num(cached);
  return null;
};

/** "Last, First" from a client row, or the line's own name as a fallback. */
const clientLabel = (client, line) => {
  const name = client
    ? [client.last_name, client.first_name].filter(Boolean).join(', ')
    : (line?.client_name || 'Unknown client');
  return client?.client_id ? `${client.client_id} ${name}` : name;
};

// ── Visit windows (caregiver checks) ────────────────────────────────────────
/**
 * The window a visit actually occupied: its clock record where there is a
 * usable one, otherwise its schedule. Which clock record counts is decided in
 * visitHours.js (clockWindow), the same rule that values the claim line.
 */
export const visitWindow = (v) => {
  const clocked = clockWindow(v);
  if (clocked) return { ...clocked, clocked: true };
  const s = v?.scheduledStartDate || v?.startDate;
  const e = v?.scheduledEndDate || v?.endDate;
  const start = s ? new Date(s) : null; const end = e ? new Date(e) : null;
  if (!start || !end || isNaN(start) || isNaN(end) || end <= start) return null;
  return { start, end, clocked: false };
};

const easternDay = (d) => d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
const easternTime = (d) => d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
const personName = (p) => (p ? [p.lastName, p.firstName].filter(Boolean).join(', ') : '');

const OVERLAP_GRACE_MS = 15 * 60 * 1000;   // a handover running a few minutes over is not two places at once
const MINOR_OVER_AUTH = 0.5;                // hours; under this it is a late clock-out, listed as a count
const EXCLUSION_STALE_DAYS = 35;            // OIG updates the LEIE monthly

/**
 * Every finding for one run.
 *
 * @param {object}   p
 * @param {object}   p.run
 * @param {object[]} p.lines         claim_lines of this run
 * @param {object[]} p.clients       clients those lines belong to
 * @param {object}   p.authHistory   prior_auth_history grouped by client id
 * @param {object[]} p.otherLines    claim_lines from OTHER runs (or manual) on the same clients and dates
 * @param {object[]|null} p.visits   AxisCare-shaped visits for the run window; null when not loaded
 * @param {object[]} p.staff         staff rows, matched to visit caregivers
 * @param {Date}     p.now
 * @returns {{ checks: object[], serious: number, review: number, visitsChecked: boolean, unknownAuthHours: string[] }}
 */
export const auditRun = ({
  run, lines = [], clients = [], authHistory = {}, otherLines = [],
  visits = null, staff = [], now = new Date(),
} = {}) => {
  const checks = [];
  const add = (key, severity, title, why, items, extra = {}) => {
    if (items.length) checks.push({ key, severity, title, why, items, ...extra });
  };
  const clientById = new Map((clients || []).map((c) => [c.id, c]));
  const billed = (lines || []).filter((l) => num(l.hours) > 0);
  const basis = run?.hours_basis || null;
  // Clients whose authorised hours are not on file — a gap in what could be
  // checked, not a finding, so it is reported beside the findings.
  let unknownAuthHours = [];

  // ── Serious ───────────────────────────────────────────────────────────────

  // Billed for a child who was not receiving care. Visits left "scheduled" in
  // AxisCare after a child goes on hold or is discharged bill on their
  // scheduled hours unless someone removes them.
  {
    const by = new Map();
    for (const l of billed) {
      const c = clientById.get(l.client_id);
      const why = !c ? 'not matched to a client record'
        : c.is_test ? 'a test record'
        : c.superseded_by ? 'a duplicate record'
        : !IN_SERVICE_STATUSES.includes(c.current_status) ? (c.current_status || 'no status')
        : null;
      if (!why) continue;
      const k = l.client_id || l.client_name;
      const e = by.get(k) || { c, l, why, hours: 0, unclocked: 0, days: new Set() };
      e.hours += num(l.hours);
      if (!(num(l.clocked_hours) > 0)) e.unclocked += num(l.hours);
      e.days.add(day(l.service_date));
      by.set(k, e);
    }
    add('not_in_service', 'serious', 'Billed for a client who is not in service',
      'A reviewer reads hours billed for a child on hold, discharged or suspended as care that did not happen. ' +
      'Usually the AxisCare schedule was never cleared. Check the date the status changed and remove anything after it.',
      [...by.values()].map((e) => ({
        text: `${clientLabel(e.c, e.l)} — ${e.why}: ${hrs(e.hours)} on ${e.days.size} day${e.days.size === 1 ? '' : 's'}` +
          (e.unclocked ? `, ${e.unclocked === e.hours ? 'none' : hrs(e.hours - e.unclocked)} clocked` : ''),
      })));
  }

  // More than 24 hours for one child in one day.
  {
    const by = new Map();
    for (const l of billed) {
      const k = `${l.client_id}|${day(l.service_date)}`;
      const e = by.get(k) || { l, hours: 0, parts: [] };
      e.hours += num(l.hours);
      e.parts.push(`${l.service_code}${l.modifier ? `-${l.modifier}` : ''} ${hrs(num(l.hours))}${num(l.clocked_hours) > 0 ? '' : ' unclocked'}`);
      by.set(k, e);
    }
    add('over_24h', 'serious', 'More than 24 hours in a day',
      'A day has 24 hours. No explanation makes this billable as it stands — one of the visits is wrong.',
      [...by.values()].filter((e) => e.hours > 24.01).map((e) => ({
        text: `${clientLabel(clientById.get(e.l.client_id), e.l)} on ${day(e.l.service_date)} — ${hrs(e.hours)} (${e.parts.join(' + ')})`,
      })));
  }

  // The same care twice: once in another run (or a manual claim), or twice in
  // this one with nothing to tell the lines apart.
  {
    const items = [];
    const key = (l) => `${l.client_id}|${day(l.service_date)}|${l.service_code}|${l.modifier || ''}`;
    const elsewhere = new Map();
    for (const o of otherLines || []) {
      if (o.billing_run_id && o.billing_run_id === run?.id) continue;
      if (!(num(o.hours) > 0)) continue;
      (elsewhere.get(key(o)) || elsewhere.set(key(o), []).get(key(o))).push(o);
    }
    const seen = new Set();
    for (const l of billed) {
      const k = key(l);
      if (elsewhere.has(k) && !seen.has(k)) {
        seen.add(k);
        const o = elsewhere.get(k)[0];
        items.push({
          text: `${clientLabel(clientById.get(l.client_id), l)} on ${day(l.service_date)}, ${l.service_code} — ` +
            `also on ${o.source === 'manual' ? 'a manual claim' : 'another run'} (${hrs(num(o.hours))})`,
        });
      }
    }
    const within = new Map();
    for (const l of billed) {
      const vid = String(l.axiscare_visit_id || l.caera_visit_id || '');
      const k = vid ? `id|${vid}` : `${key(l)}|${num(l.hours)}|${num(l.scheduled_hours)}|${num(l.clocked_hours)}`;
      (within.get(k) || within.set(k, []).get(k)).push(l);
    }
    for (const [k, group] of within) {
      if (group.length < 2) continue;
      const l = group[0];
      items.push({
        text: `${clientLabel(clientById.get(l.client_id), l)} on ${day(l.service_date)}, ${l.service_code} — ` +
          `${group.length} lines ${k.startsWith('id|') ? 'for the same visit' : `of exactly ${hrs(num(l.hours))}`}`,
      });
    }
    add('billed_twice', 'serious', 'The same care billed twice',
      'Duplicate billing is the first thing claims software looks for. ' +
      'Two lines on one day can be a genuine split shift; two identical ones, or one already in another run, are not.',
      items);
  }

  // ── Caregiver checks (need the visits) ────────────────────────────────────
  const visitsChecked = Array.isArray(visits);
  const caregiverRows = [];
  if (visitsChecked) {
    const billedHere = new Set(billed.map((l) => `${day(l.service_date)}`));
    for (const v of visits) {
      if (v.removed || !v.caregiver) continue;
      if (!(num(v.chargeRate) > 0)) continue;
      const w = visitWindow(v);
      if (!w) continue;
      const d = easternDay(w.start);
      if (!billedHere.has(d)) continue;
      caregiverRows.push({ v, w, d, cg: String(v.caregiver.id ?? personName(v.caregiver)) });
    }

    // One person in two homes at once.
    const byCg = new Map();
    for (const r of caregiverRows) (byCg.get(r.cg) || byCg.set(r.cg, []).get(r.cg)).push(r);
    const overlaps = [];
    for (const rows of byCg.values()) {
      rows.sort((a, b) => a.w.start - b.w.start);
      for (let i = 0; i < rows.length; i++) {
        for (let j = i + 1; j < rows.length && rows[j].w.start < rows[i].w.end; j++) {
          const a = rows[i]; const b = rows[j];
          if (String(a.v.client?.id) === String(b.v.client?.id)) continue;
          const ms = Math.min(a.w.end, b.w.end) - Math.max(a.w.start, b.w.start);
          if (ms <= OVERLAP_GRACE_MS) continue;
          const sameHome = a.v.client?.lastName && a.v.client.lastName === b.v.client?.lastName;
          overlaps.push({
            text: `${personName(a.v.caregiver)} on ${a.d} — ${personName(a.v.client)} ` +
              `${easternTime(a.w.start)}–${easternTime(a.w.end)} and ${personName(b.v.client)} ` +
              `${easternTime(b.w.start)}–${easternTime(b.w.end)}, overlapping ${hrs(ms / 3600000)}` +
              (sameHome ? ' (same surname — siblings in one home?)' : ''),
          });
        }
      }
    }
    add('caregiver_overlap', 'serious', 'One caregiver in two places at once',
      'The classic impossible claim. Usually a wrong time in AxisCare; fix the times before filing. ' +
      'Siblings cared for together in one home are a GAPP policy question — confirm it is allowed before billing both.',
      overlaps);

    // Days and weeks no one person can work.
    const dayTotals = new Map(); const weekTotals = new Map();
    for (const r of caregiverRows) {
      const h = (r.w.end - r.w.start) / 3600000;
      const dk = `${r.cg}|${r.d}`; const wk = `${r.cg}|${weekOf(r.d)}`;
      const de = dayTotals.get(dk) || { r, h: 0 }; de.h += h; dayTotals.set(dk, de);
      const we = weekTotals.get(wk) || { r, h: 0, wk: weekOf(r.d) }; we.h += h; weekTotals.set(wk, we);
    }
    add('caregiver_over_24h', 'serious', 'A caregiver over 24 hours in a day',
      'Nobody works more than 24 hours in a day. One of the visits has the wrong times.',
      [...dayTotals.values()].filter((e) => e.h > 24.01)
        .map((e) => ({ text: `${personName(e.r.v.caregiver)} on ${e.r.d} — ${hrs(e.h)}` })));
    const longDays = [...dayTotals.values()].filter((e) => e.h > 16.01 && e.h <= 24.01);
    const longWeeks = [...weekTotals.values()].filter((e) => e.h > 60.01);
    add('caregiver_long_hours', 'review', 'Very long caregiver hours',
      'Not wrong in itself, but a caregiver working 16-hour days or 60-hour weeks is a peer outlier and the first person a reviewer asks about. ' +
      'Make sure the clock records and notes for these visits are complete.',
      [
        ...longWeeks.map((e) => ({ text: `${personName(e.r.v.caregiver)} — ${hrs(e.h)} in the week of ${e.wk}` })),
        ...(longDays.length ? [{ text: `${longDays.length} day${longDays.length === 1 ? '' : 's'} over 16 hours: ` +
          [...new Set(longDays.map((e) => personName(e.r.v.caregiver)))].join('; ') }] : []),
      ]);

    // Who delivered the care, and whether CAERA can prove they were cleared to.
    const staffByAxis = new Map(); const staffById = new Map();
    for (const s of staff || []) {
      if (s.axiscare_id != null && s.axiscare_id !== '') staffByAxis.set(String(s.axiscare_id), s);
      staffById.set(s.id, s);
    }
    const people = new Map();
    for (const r of caregiverRows) people.set(r.cg, r.v.caregiver);
    const stale = now.getTime() - EXCLUSION_STALE_DAYS * 86400000;
    const noExclusion = []; const noLicence = []; const unmatched = [];
    for (const cg of people.values()) {
      const s = staffById.get(cg.staffId) || staffByAxis.get(String(cg.id));
      if (!s) { unmatched.push(personName(cg)); continue; }
      const name = (s.last_name && s.first_name) ? `${s.last_name}, ${s.first_name}` : (personName(cg) || s.full_name);
      if (!s.exclusion_checked_at || new Date(s.exclusion_checked_at).getTime() < stale) noExclusion.push(name);
      if (['RN', 'LPN'].includes(s.position_code) && !s.license_verified_at) noLicence.push(name);
    }
    add('staff_checks', 'review', 'No proof the caregiver was cleared to work',
      'Billing for care by an excluded person is an overpayment however good the care was, and a licence lapse on a nurse is the same. ' +
      `CAERA should hold a check against the OIG and Georgia exclusion lists from the last ${EXCLUSION_STALE_DAYS} days, and a licence verification for every nurse. ` +
      'If the checks are done elsewhere, record them on the staff profile so they can be shown.',
      [
        ...(noExclusion.length ? [{ text: `No exclusion check in the last ${EXCLUSION_STALE_DAYS} days (${noExclusion.length}): ${noExclusion.join('; ')}` }] : []),
        ...(noLicence.length ? [{ text: `Nurse with no licence verification on record (${noLicence.length}): ${noLicence.join('; ')}` }] : []),
        ...(unmatched.length ? [{ text: `In AxisCare but not matched to a CAERA staff record (${unmatched.length}): ${unmatched.join('; ')}` }] : []),
      ]);
  }

  // ── To review ─────────────────────────────────────────────────────────────

  // Hours with no clock-in behind them. Lines from before hours were recorded
  // per basis carry no clocked figure at all and are not counted as unclocked.
  if (basis && basis !== 'clocked') {
    const by = new Map(); let total = 0; let all = 0;
    for (const l of billed) {
      all += num(l.hours);
      if (l.clocked_hours == null || num(l.clocked_hours) > 0) continue;
      const e = by.get(l.client_id) || { l, hours: 0, n: 0 };
      e.hours += num(l.hours); e.n += 1; total += num(l.hours);
      by.set(l.client_id, e);
    }
    add('no_clock', 'review', 'Billed with no clock-in',
      `${hrs(total)} of ${hrs(all)} in this run (${all ? Math.round((total / all) * 100) : 0}%) were billed on scheduled hours with no clock record. ` +
      'Care delivered without a clock-in is still care, but EVV that does not match the claim is the most common data flag. ' +
      'Each one needs a reason and a note that shows the visit happened.',
      [...by.values()].sort((a, b) => b.hours - a.hours).map((e) => ({
        text: `${clientLabel(clientById.get(e.l.client_id), e.l)} — ${hrs(e.hours)} on ${e.n} visit${e.n === 1 ? '' : 's'}`,
      })));
  }

  // Billed above what was clocked: a run on scheduled hours bills the planned
  // window even when the caregiver clocked out early.
  {
    const over = billed.filter((l) => num(l.clocked_hours) > 0 && num(l.hours) > num(l.clocked_hours) + 0.25);
    add('above_clocked', 'review', 'Billed above the clocked hours',
      'The claim says more time than the clock record. If the clock is right, the claim is an overbilling.',
      over.map((l) => ({
        text: `${clientLabel(clientById.get(l.client_id), l)} on ${day(l.service_date)}, ${l.service_code} — ` +
          `billed ${hrs(num(l.hours))}, clocked ${hrs(num(l.clocked_hours))}`,
      })));
  }

  // Above the authorised weekly hours, and authorisations that cannot be read.
  {
    const by = new Map();
    for (const l of billed) {
      const bucket = authBucket(l.service_code);
      if (!bucket) continue;
      const d = day(l.service_date);
      const k = `${l.client_id}|${weekOf(d)}|${bucket}`;
      const e = by.get(k) || { l, bucket, week: weekOf(d), hours: 0, firstDate: d };
      e.hours += num(l.hours);
      if (d < e.firstDate) e.firstDate = d;
      by.set(k, e);
    }
    const material = []; let minor = 0; const unknown = new Set(); const noAuth = new Map();
    for (const e of by.values()) {
      const client = clientById.get(e.l.client_id);
      const history = authHistory[e.l.client_id] || [];
      const auth = authorisedWeeklyHours({ history, client, date: e.firstDate, bucket: e.bucket });
      if (auth == null) { unknown.add(clientLabel(client, e.l)); continue; }
      const excess = e.hours - auth;
      if (excess >= MINOR_OVER_AUTH) material.push({ e, auth, excess, client });
      else if (excess > 0.01) minor += 1;
    }
    for (const l of billed) {
      const client = clientById.get(l.client_id);
      const a = resolvePriorAuth({ history: authHistory[l.client_id] || [], client, date: day(l.service_date) });
      if (!a.number && !String(l.prior_auth_number || '').trim()) {
        const k = l.client_id;
        const e = noAuth.get(k) || { l, client, hours: 0, from: day(l.service_date), to: day(l.service_date) };
        e.hours += num(l.hours);
        if (day(l.service_date) < e.from) e.from = day(l.service_date);
        if (day(l.service_date) > e.to) e.to = day(l.service_date);
        noAuth.set(k, e);
      }
    }
    add('over_auth', 'review', 'Above the authorised weekly hours',
      'Hours past the authorisation are not payable, and billing them week after week reads as a pattern. ' +
      'Small overruns come from clocking out a few minutes late; larger ones need an amended schedule or authorisation.',
      [
        ...material.sort((a, b) => b.excess - a.excess).map(({ e, auth, excess, client }) => ({
          text: `${clientLabel(client, e.l)}, week of ${e.week} — ${e.bucket === 'skilled' ? 'skilled nursing' : 'personal care'} ` +
            `${hrs(e.hours)} against ${hrs(auth)} authorised (+${hrs(excess)})`,
        })),
        ...(minor ? [{ text: `${minor} more client-week${minor === 1 ? '' : 's'} over by less than 30 minutes` }] : []),
      ]);
    unknownAuthHours = [...unknown];
    add('no_auth', 'review', 'No authorisation in force',
      'GAMMIS denies these (CO-15). Billing care with no authorisation, repeatedly, is a pattern in its own right. ' +
      'Hold them until the renewal is issued unless it will be dated to cover the gap.',
      [...noAuth.values()].map((e) => ({
        text: `${clientLabel(e.client, e.l)} — ${hrs(e.hours)}, ${e.from === e.to ? e.from : `${e.from} to ${e.to}`}`,
      })));
  }

  return {
    checks,
    serious: checks.filter((c) => c.severity === 'serious').length,
    review: checks.filter((c) => c.severity === 'review').length,
    visitsChecked,
    unknownAuthHours,
  };
};

/** The order a person should read them in. */
export const AUDIT_ORDER = [
  'over_24h', 'caregiver_over_24h', 'billed_twice', 'caregiver_overlap', 'not_in_service',
  'no_auth', 'over_auth', 'above_clocked', 'no_clock', 'caregiver_long_hours', 'staff_checks',
];
